import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * Move a platform parameter.
 *
 * The write that `platform.setting` never had. Every reader on this platform —
 * the withdrawal payout-ratio hold, the KYC auto-pass confidence, the affiliate
 * quality-review threshold — reads a row that only `seed.ts` and one hand-written
 * SQL file had ever written, so the values on the screen behind this dialog were
 * unchangeable and `platform.setting_history`, the configuration log the design
 * requires, contained nothing but fixtures.
 *
 * THREE THINGS THIS DIALOG SAYS THAT ARE TRUE OF THE ROUTE, not decoration:
 *
 *   · IT TAKES EFFECT IMMEDIATELY. There is no scheduler, no pending-change
 *     table and no maker-checker for configuration — `platform.setting_change_request`
 *     does not exist. The value is live for every reader the moment this saves.
 *   · IT IS LOGGED WITH THE ACTOR, in the same transaction as the value. The
 *     log below the table on this screen is what reads it back.
 *   · IT CANNOT CREATE A PARAMETER. The key comes from the row that was clicked
 *     and the route refuses any key that does not already exist, so a typo
 *     cannot make a parameter nothing reads.
 */

export interface EditableSetting {
  key: string;
  value: string;
  per_brand: boolean;
  description: string | null;
  /** NULL brand_id => the platform-wide row. A name here means it is an override. */
  brand: string | null;
  /** The id behind that name — what an override edit addresses itself with. */
  brand_id: string | null;
}

export function EditSettingDialog({
  setting,
  brands,
  onClose,
  onDone,
}: {
  setting: EditableSetting | null;
  /** Every brand this admin may act on, for the override target. */
  brands: { id: string; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [value, setValue] = useState('');
  /** '' means the platform-wide row; an id means that brand's override. */
  const [target, setTarget] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValue(setting?.value ?? '');
    setTarget('');
    setFields({});
  }, [setting]);

  const close = () => {
    setFields({});
    onClose();
  };

  const submit = async () => {
    if (!setting) return;
    if (value.trim() === '') {
      setFields({ value: 'Give the parameter a value.' });
      return;
    }
    if (target === '' && value.trim() === setting.value) {
      setFields({
        value:
          'That is the value it already holds. A configuration log entry that records no change is noise in the one place that has to stay readable.',
      });
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<{
        key: string;
        value: string;
        brandName: string | null;
        valueBefore: string | null;
        overrideCreated: boolean;
      }>('/api/platform/settings', {
        key: setting.key,
        value: value.trim(),
        /* EDIT THE ROW THAT WAS OPENED.
           `target` is the override PICKER, and it only appears on the
           platform-wide row. Opened on an existing override it stayed empty, so
           no brandId was sent and the write landed on the platform-wide value —
           silently moving withdrawal.payout_ratio_hold or
           kyc.auto_pass_threshold for EVERY brand while the dialog said it was
           changing one. An override row addresses itself. */
        ...(setting.brand_id !== null
          ? { brandId: setting.brand_id }
          : target === ''
            ? {}
            : { brandId: target }),
      });
      toast(
        result.brandName
          ? `${result.key} is ${result.value} for ${result.brandName}${
              result.overrideCreated ? ' — a new override' : ''
            }. Logged against you.`
          : `${result.key} moved ${result.valueBefore ?? '—'} → ${result.value}. Logged against you.`,
      );
      onDone();
      close();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not change the parameter',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed, and nothing was logged. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  /* An override may only be written where the platform-wide row allows one, and
     only from the platform-wide row itself: editing an existing override is a
     change to that brand's value, not a chance to pick a different brand. */
  const mayOverride = setting !== null && setting.per_brand && setting.brand === null;
  const targetName = brands.find((b) => b.id === target)?.name ?? null;

  return (
    <Dialog
      open={setting !== null}
      onClose={close}
      title={setting ? `Change ${setting.key}` : 'Change parameter'}
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            {targetName ? `Save for ${targetName}` : 'Save the change'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            {setting?.description ?? 'No description is recorded on this parameter.'}
          </div>
        </div>

        {mayOverride ? (
          <Field
            label="Applies to"
            hint="This parameter allows a per-brand override. A brand's own value takes precedence; every other brand keeps the platform-wide one."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">Every brand — the platform-wide value</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} only
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {/* NO UNIT AND NO TYPE IS THE SCHEMA'S DOING, NOT THIS FORM'S.
            `platform.setting.value` is a `text` column. Nothing is recorded
            beside it saying whether a parameter is a ratio, a count, a
            percentage or a weekday, and `POST /api/platform/settings`
            deliberately validates none of them, on the reasoning that a
            validator here would be a second and weaker copy of whatever the
            reader actually does with the value. So the warning has to reach
            the operator in the hint, because the only check that will ever run
            on what they type is the reader that eventually parses it. Naming
            the column would not help them: an operator cannot act on it, and
            "plain text with no unit recorded" is the same fact in words they
            already have. */}
        <Field
          label="Value"
          error={fields['value']}
          hint="Stored exactly as typed. The value is kept as plain text, with no unit or type recorded beside it, so whatever reads this parameter interprets what you type for itself — nothing here checks that it is a number, or in the units that reader expects."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="tabular"
            />
          )}
        </Field>

        {setting && target === '' && setting.brand === null ? (
          <div className="text-subtle" style={{ fontSize: '12.5px' }}>
            Currently <span className="tabular">{setting.value}</span> for every brand.
          </div>
        ) : null}

        <div className="notice">
          <Icon name="warning-circle" />
          <div>
            This takes effect <strong>immediately</strong> everywhere this parameter is read —
            there is no schedule, no staging and no second approver. A configuration change is
            never parked for somebody else to approve, the way a payout is, because this platform
            keeps no record of a change that is waiting. The change is recorded in the
            configuration log with your name, the value before and the value after, written in the
            same step as the value itself.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
